import type { UpdateUserSettingsRequest, RecentWorkflow, UserInfo, Tags } from '../types/auth';
import type { User, UserPatch, UsersListResponse } from '../types/users';
import { VL_API_BASE_URL } from '../constants/backendUrls';
import { APIClient } from './apiClient';
import { camelCaseToKebabCase } from 'vince-core/utils/string';

const baseUrl = `${VL_API_BASE_URL}/users`;
const baseTagsUrl = `${VL_API_BASE_URL}/v2/tags/user`;

export async function updateUserProperty (data: string[] | string | RecentWorkflow[], property: string, userId: string, tenantId: string, del: boolean = false): Promise<User> {
    const request = {
        [property]: data,
        userId,
        tenantId
    };
    const method = del ? 'DELETE' : 'PATCH';
    const propertyName = camelCaseToKebabCase(property);

    // @ts-ignore Gives tsc recursion error
    return await APIClient(baseUrl + '/me/' + propertyName, { method, body: request });
}

export async function updateUserProperties (
    properties: Partial<UserInfo>,
    userId: string,
    tenantId: string
): Promise<User> {
    const request = {
        ...properties,
        userId,
        tenantId
    };

    return await APIClient(`${baseUrl}/me`, { method: 'PATCH', body: JSON.stringify(request) });
}

export async function saveUserSettings (data: UpdateUserSettingsRequest) {
    return await APIClient<UserInfo>(`${baseUrl}/me/user-settings`, { method: 'PATCH', body: JSON.stringify(data) });
}

export async function loadUsers () {
    const allUsers: User[] = [];
    let startKey: string | undefined;

    do {
        const params = new URLSearchParams({ limit: '500' });
        if (startKey) {
            params.append('startKey', startKey);
        }
        const response = await APIClient<UsersListResponse>(`${baseUrl}?${params.toString()}`);
        allUsers.push(...response.items);
        startKey = response.lastKey;
    } while (startKey);

    return { items: allUsers };
}

export async function getTenantUserTagsKeys () {
    return await APIClient<{entity: string, type: string, data: string[]}>(`${baseTagsUrl}/keys`);
}

export async function updateUser (data: User) {
    try {
        const response = await APIClient<User>(`${baseUrl}/`, {
            method: 'PUT',
            body: JSON.stringify(data),
            headers: {
                'Content-Type': 'application/json'
            }
        });

        return response;
    } catch (error) {
        console.error('Error updating user:', error);
        throw error;
    }
}

export async function createUser (data: User) {
    try {
        const response = await APIClient<User>(`${baseUrl}/`, {
            method: 'POST',
            body: JSON.stringify(data),
            headers: {
                'Content-Type': 'application/json'
            }
        });

        return response;
    } catch (error) {
        console.error('Error creating user:', error);
        throw error;
    }
}

export async function saveUser (data: User) {
    if (data.userId) {
        return await updateUser(data);
    } else {
        return await createUser(data);
    }
}

export async function getUserTags (data:string) {
    return await APIClient(`${baseTagsUrl}/${data}`);
}

export async function getAvailableTags () {
    return await APIClient<{data: string[]}>(`${baseTagsUrl}/keys`);
}

// TODO
export async function getUserById (userId: string) {
    return await APIClient<User>(`${baseUrl}/${userId}`);
}

export async function getUserTagsById (data:string) {
    const response = await APIClient(`${baseTagsUrl}/${data}`);
    return response;
}

export async function getUsersByEmail (email: string) {
    return await APIClient<UsersListResponse>(`${baseUrl}?search=email.keyword:${encodeURIComponent(`"${email}"`)}`);
}

export async function activateDeactivateUser (userId: string, tenantId: string, active: boolean) {
    return await APIClient<User>(`${baseUrl}/`, {
        method: 'PATCH',
        body: {
            userId,
            tenantId,
            active
        }
    });
}

export async function updateUserTags (userId: string, tenantId: string, tags: Tags) {
    return await APIClient<User>(`${baseUrl}/`, {
        method: 'PATCH',
        body: {
            userId,
            tenantId,
            tags
        }
    });
}

export async function patchUser (patch: UserPatch) {
    return await APIClient<User>(`${baseUrl}/`, { method: 'PATCH', body: patch });
}

export async function inviteUser (userId: string, tenantId: string) {
    return await APIClient<User>(`${baseUrl}/${userId}/invite`, {
        method: 'PATCH',
        body: {
            userId,
            tenantId
        }
    });
}
